import React, { useCallback, useMemo, useState } from 'react';
import { TableCellsIcon } from '@heroicons/react/24/outline';
import { useClientExportFlow } from '../../hooks/useClientExportFlow';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import type { KpiLastUpdatedRow, KpiOperationalAgeingRow } from './types';
import KpiBacklogTableModal from './KpiBacklogTableModal';
import {
  KpiOperationalAgeingTable,
  KpiOperationalLastUpdatedTable,
  exportAgeingTableExcel,
  exportLastUpdatedTableExcel,
} from './KpiOperationalSummaryTables';
import { fetchOpenBacklogOperationalDetail } from './kpiDashboardApi';
import { buildOpenBacklogDetailExcel } from './kpiOpenBacklogDetailExcel';

export interface KpiOpenBacklogSectionProps {
  ageingByGroup: KpiOperationalAgeingRow[];
  lastUpdatedByGroup: KpiLastUpdatedRow[];
  filterValues: Record<string, string | string[]>;
}

type BacklogModal = 'none' | 'ageing' | 'lastUpdated';

const KpiOpenBacklogSection: React.FC<KpiOpenBacklogSectionProps> = ({
  ageingByGroup,
  lastUpdatedByGroup,
  filterValues,
}) => {
  const [modal, setModal] = useState<BacklogModal>('none');
  const { preferences } = useDateFormatter();
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);
  const { run, modal: exportModal } = useClientExportFlow();

  const bothEmpty = ageingByGroup.length === 0 && lastUpdatedByGroup.length === 0;
  const hasAgeing = ageingByGroup.length > 0;
  const hasLastUpdated = lastUpdatedByGroup.length > 0;

  const exportAgeingSummaryExcel = useCallback(() => {
    void run({
      downloadButtonLabel: 'Download Excel',
      work: async () => ({
        files: exportAgeingTableExcel(ageingByGroup),
        summaryLines: ['Your summary Excel file is ready to download.'],
      }),
    });
  }, [run, ageingByGroup]);

  const exportLastUpdatedSummaryExcel = useCallback(() => {
    void run({
      downloadButtonLabel: 'Download Excel',
      work: async () => ({
        files: exportLastUpdatedTableExcel(lastUpdatedByGroup),
        summaryLines: ['Your summary Excel file is ready to download.'],
      }),
    });
  }, [run, lastUpdatedByGroup]);

  const exportAgeingDetailExcel = useCallback(() => {
    void run({
      downloadButtonLabel: 'Download Excel',
      work: async () => {
        const { rows, truncated, totalMatched } = await fetchOpenBacklogOperationalDetail(
          filterValues,
          timezoneOffset,
          'ageing'
        );
        if (rows.length === 0) {
          throw new Error('No tickets to export for the current filters.');
        }
        const files = buildOpenBacklogDetailExcel(rows, 'ageing', preferences);
        const summaryLines: string[] = [`${rows.length.toLocaleString()} ticket row(s).`];
        if (truncated && totalMatched !== undefined) {
          summaryLines.push(
            `Includes first ${rows.length.toLocaleString()} of ${totalMatched.toLocaleString()} matched tickets.`
          );
        }
        summaryLines.push('Your detail Excel file is ready to download.');
        return { files, summaryLines };
      },
    });
  }, [run, filterValues, preferences, timezoneOffset]);

  const exportLastUpdatedDetailExcel = useCallback(() => {
    void run({
      downloadButtonLabel: 'Download Excel',
      work: async () => {
        const { rows, truncated, totalMatched } = await fetchOpenBacklogOperationalDetail(
          filterValues,
          timezoneOffset,
          'lastUpdated'
        );
        if (rows.length === 0) {
          throw new Error('No tickets to export for the current filters.');
        }
        const files = buildOpenBacklogDetailExcel(rows, 'lastUpdated', preferences);
        const summaryLines: string[] = [`${rows.length.toLocaleString()} ticket row(s).`];
        if (truncated && totalMatched !== undefined) {
          summaryLines.push(
            `Includes first ${rows.length.toLocaleString()} of ${totalMatched.toLocaleString()} matched tickets.`
          );
        }
        summaryLines.push('Your detail Excel file is ready to download.');
        return { files, summaryLines };
      },
    });
  }, [run, filterValues, preferences, timezoneOffset]);

  return (
    <>
      {exportModal}
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-950/[0.04] sm:p-5">
          <h3 className="text-sm font-semibold text-slate-900">Open backlog summaries</h3>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">
            Open incidents and service requests in New, In Progress, Open, or On Hold only. Counts respect your filters
            (assignment group, customer, priority, state, assignee). They do not use the KPI date range so they reflect
            the current backlog snapshot.
          </p>

          {!bothEmpty && (
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={!hasAgeing}
                onClick={() => setModal('ageing')}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <TableCellsIcon className="h-5 w-5 text-indigo-600" aria-hidden />
                &gt;2 days ageing ticket summary
              </button>
              <button
                type="button"
                disabled={!hasLastUpdated}
                onClick={() => setModal('lastUpdated')}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <TableCellsIcon className="h-5 w-5 text-indigo-600" aria-hidden />
                Last time ticket updated
              </button>
            </div>
          )}
        </div>

        {bothEmpty && (
          <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center text-sm text-slate-500">
            No open incidents or service requests match the current filters (or your ticket type filter excludes both).
          </p>
        )}

        <KpiBacklogTableModal
          isOpen={modal === 'ageing'}
          onClose={() => setModal('none')}
          title=">2 days ageing ticket summary (by assignment group)"
          subtitle="New, In Progress, Open, and On Hold only. Age is measured from ticket created time. Buckets include tickets at least 2 days old (0–2 day backlog is excluded here)."
          onExportSummaryExcel={exportAgeingSummaryExcel}
          exportDisabled={!hasAgeing}
          onExportDetailExcel={exportAgeingDetailExcel}
          detailExportDisabled={!hasAgeing}
        >
          <KpiOperationalAgeingTable ageingByGroup={ageingByGroup} />
        </KpiBacklogTableModal>

        <KpiBacklogTableModal
          isOpen={modal === 'lastUpdated'}
          onClose={() => setModal('none')}
          title="Last time ticket updated (by assignment group)"
          subtitle="Same open backlog as above (New, In Progress, Open, On Hold). Compares the ticket last update time to now (0–24 hours vs older)."
          onExportSummaryExcel={exportLastUpdatedSummaryExcel}
          exportDisabled={!hasLastUpdated}
          onExportDetailExcel={exportLastUpdatedDetailExcel}
          detailExportDisabled={!hasLastUpdated}
        >
          <KpiOperationalLastUpdatedTable lastUpdatedByGroup={lastUpdatedByGroup} />
        </KpiBacklogTableModal>
      </div>
    </>
  );
};

export default KpiOpenBacklogSection;
